import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { twMerge } from 'tailwind-merge';

import { MAX_NAME_LENGTH } from '~/consts';
import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import { Icon } from '~/modules/elements/akui/icon';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import useDebounce from '~/modules/hooks/use-debounce';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { RemotePlaylistEntry } from '~/modules/remote-mic/network/client/subscriptions';
import { HelpEntry } from '~/routes/keyboard-help/context';
import { onPress, onRepeat, vibrate } from '~/routes/remote-mic/panels/microphone/remote-actions';
import RingController from '~/routes/remote-mic/panels/microphone/ring-controller';
import { screenTransition } from '~/routes/remote-mic/panels/microphone/screen-transition';

const SURFACE = 'bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]';
const ICON_BUTTON = `flex size-13 shrink-0 cursor-pointer items-center justify-center rounded-2xl border-none text-white transition-transform duration-100 active:scale-[0.94] disabled:cursor-default disabled:opacity-40`;

// The microphone tab on the song list and on arrows-only screens. Back starts the row like a
// browser's, so everything sits at the top and the ring takes what is left.
export default function SongSelectionScreen({ keyboard, classic }: { keyboard: HelpEntry; classic: boolean }) {
  const songSelection = useSubscription('song-selection');
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];

  return (
    <motion.div
      data-test="remote-keyboard"
      data-mode={classic ? 'classic' : 'song-selection'}
      className="flex h-full min-h-0 w-full flex-col gap-3"
      {...screenTransition}>
      <div className="flex shrink-0 gap-2.5">
        <button
          type="button"
          aria-label={keyboard.back || 'Back'}
          onClick={onPress('back')}
          disabled={keyboard.back === undefined}
          data-test="keyboard-backspace"
          className={twMerge(ICON_BUTTON, 'bg-black/50 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]')}>
          <Icon icon="ic:baseline-arrow-back" size={5.5} />
        </button>
        {!classic && (
          <>
            <SearchField enabled={!!keyboard.remote?.includes('search')} />
            <button
              type="button"
              aria-label="Random song"
              disabled={keyboard.shiftR === undefined || !keyboard.remote?.includes('search')}
              onClick={onPress('random')}
              data-test="keyboard-shift-r"
              className={twMerge(ICON_BUTTON, SURFACE, 'active:-rotate-12')}>
              <Icon icon="ic:baseline-casino" size={5.5} />
            </button>
          </>
        )}
      </div>
      {!classic && (
        <PlaylistPicker
          playlists={songSelection?.playlists ?? []}
          selectedPlaylist={songSelection?.selectedPlaylist ?? null}
        />
      )}
      <NavRing keyboard={keyboard} />
      {playerNumber === null && (
        <div
          data-test="remote-only-note"
          className="flex shrink-0 items-center justify-center gap-2 text-center text-[0.875rem] text-white/75">
          <Icon icon="ic:outline-info" size={4.5} className="text-active shrink-0" />
          <span>Remote only. To sing, pick a slot in Setup microphones on the TV.</span>
        </div>
      )}
    </motion.div>
  );
}

// Typing is sent a beat after it stops. The field opens empty and says so to the television too, so
// the room is never left looking at a list filtered by a box nobody can see.
function SearchField({ enabled }: { enabled: boolean }) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 100);
  const previousSearch = usePrevious(debouncedSearch);

  useEffect(() => {
    if (previousSearch !== debouncedSearch) {
      void serverRpc.songs.search(debouncedSearch.trim());
    }
  }, [previousSearch, debouncedSearch]);

  const typing = enabled && search !== '';

  return (
    <div
      className={twMerge(
        'flex h-13 min-w-0 flex-1 items-center gap-2.5 rounded-2xl bg-black/60 pr-2.5 pl-3.5 transition-shadow duration-150',
        typing ? 'shadow-[inset_0_0_0_2px_#facc15]' : 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]',
        !enabled && 'opacity-40',
      )}>
      <Icon icon="ic:baseline-search" size={5.5} className="shrink-0 text-white/50" />
      <input
        type="text"
        value={enabled ? search : ''}
        disabled={!enabled}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setSearch('');
        }}
        maxLength={MAX_NAME_LENGTH}
        placeholder={enabled ? 'Search for a song…' : 'No search on this screen'}
        data-test="search-song-input"
        className="h-full min-w-0 flex-1 border-none bg-transparent text-[1.0625rem] text-white outline-none placeholder:text-white/45"
      />
      {typing && (
        <button
          type="button"
          aria-label="Clear search"
          onMouseDown={(event) => {
            // Ahead of the input's blur, which would otherwise swallow the click.
            event.preventDefault();
            setSearch('');
          }}
          data-test="close-search-song-button"
          className="flex size-9 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-white">
          <Icon icon="ic:baseline-close" size={5.5} />
        </button>
      )}
    </div>
  );
}

// Selection is applied optimistically: the phone shows the new playlist at once and the host's own
// `song-selection` push confirms it a round trip later.
function PlaylistPicker({
  playlists,
  selectedPlaylist,
}: {
  playlists: RemotePlaylistEntry[];
  selectedPlaylist: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [optimisticPlaylist, setOptimisticPlaylist] = useState<string | null>(null);

  // Dropped as soon as the host echoes any playlist back - the one that was picked, or the one that
  // is actually active if the host refused it.
  const [previousSelected, setPreviousSelected] = useState(selectedPlaylist);
  if (previousSelected !== selectedPlaylist) {
    setPreviousSelected(selectedPlaylist);
    setOptimisticPlaylist(null);
  }

  const activePlaylist = optimisticPlaylist ?? selectedPlaylist;
  const activeLabel = playlists.find((playlist) => playlist.name === activePlaylist)?.label ?? activePlaylist ?? 'All';

  const select = (name: string) => {
    vibrate();
    setOptimisticPlaylist(name);
    setOpen(false);
    void serverRpc.songs.setPlaylist(name);
  };

  return (
    <>
      <button
        type="button"
        disabled={playlists.length === 0}
        onClick={() => setOpen(true)}
        data-test="playlist-picker-trigger"
        className={twMerge(
          'flex h-13 shrink-0 cursor-pointer items-center gap-2.5 rounded-2xl border-none px-4.5 text-left text-white transition-transform duration-100 active:scale-[0.98] disabled:opacity-40',
          SURFACE,
        )}>
        <span className="shrink-0 text-[0.9375rem] text-white/60">Playlist</span>
        <span className="min-w-0 flex-1 truncate text-[1.0625rem] font-bold">{activeLabel}</span>
        <Icon icon="ic:baseline-expand-more" size={5.5} className="shrink-0 opacity-70" />
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title="Playlists">
        <div className="flex flex-col overflow-hidden rounded-[1.125rem] bg-white/6 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/8">
          {playlists.map((playlist) => {
            const active = playlist.name === activePlaylist;

            return (
              <button
                key={playlist.name}
                type="button"
                onClick={() => select(playlist.name)}
                data-test={`playlist-sheet-${playlist.name}`}
                data-active={active}
                className="flex h-15 shrink-0 items-center gap-3 border-none bg-transparent px-4.5 text-left text-white transition-colors duration-150 active:bg-white/6">
                <span className={`min-w-0 flex-1 truncate text-[1.0625rem] ${active ? 'font-bold' : ''}`}>
                  {playlist.label}
                </span>
                {playlist.count !== undefined && (
                  <span className="shrink-0 text-[0.875rem] text-white/50">{playlist.count}</span>
                )}
                {active && <Icon icon="ic:baseline-check" size={5} className="text-active shrink-0" />}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}

function NavRing({ keyboard }: { keyboard: HelpEntry }) {
  const horizontal = keyboard.horizontal !== undefined || keyboard['horizontal-vertical'] !== undefined;
  const vertical = keyboard.vertical !== undefined || keyboard['horizontal-vertical'] !== undefined;

  return (
    <RingController
      onPress={onPress}
      onRepeat={onRepeat}
      horizontal={horizontal}
      vertical={vertical}
      acceptEnabled={keyboard.accept !== undefined}
    />
  );
}
